import React from 'react';

import { GoogleTagManager } from '@iwoca/frontend-tracking-library';
import _ from 'lodash';
import PropTypes from 'prop-types';
import * as Redux from 'react-redux';
import { Router, browserHistory } from 'react-router';

import { selectStep } from './actions';
import SignupContainer from './containers/Containers';
import { isNewOffersEnabled } from './containers/Offers/offers/offersHelpers';
import IwTracking from './tracking/IwTracking';

export const STEP_ZERO_STEP_ID = 'stepZero';
export const BUSINESS_INTRODUCTION_STEP_ID = 'businessIntroduction';
export const PERSONAL_INFORMATION_STEP_ID = 'personalInformation';
export const BUSINESS_ACCOUNTS_STEP_ID = 'businessAccounts';
export const CHECK_ELIGIBILITY_STEP_ID = 'checkEligibility';
export const MY_ACCOUNTS_STEP_ID = 'myAccount';

// TODO: Refactor ALL_STEPS to include step zero.
export const ALL_STEPS = [
    {
        id: BUSINESS_INTRODUCTION_STEP_ID,
        name: 'Introduce your business',
        key: '1'
    },
    {
        id: PERSONAL_INFORMATION_STEP_ID,
        name: 'Tell us about yourself',
        key: '2'
    },
    {
        id: 'productOffers',
        isConditional: true,
        condition: (state) => isNewOffersEnabled(state)
    },
    {
        id: CHECK_ELIGIBILITY_STEP_ID,
        name: 'Check eligibility',
        isConditional: true
    },
    {
        id: BUSINESS_ACCOUNTS_STEP_ID,
        name: 'Verify your trading history',
        key: '4'
    },
    {
        id: MY_ACCOUNTS_STEP_ID,
        name: 'My Account',
        key: '5'
    }
];

export const STEPS = ALL_STEPS.filter(
    (step) => !_.get(step, 'isConditional', false)
);

const App = ({ children }) => children;
const MyAccountContainer = () => null;

/**
 * @url http://redux.js.org/docs/advanced/UsageWithReactRouter.html
 * @note Redux and React Router:
 * In most of the cases, it is fine to have them separate unless you need to time travel and rewind actions that triggers the change URL.
 *
 * Note About Other Routing Libraries
 * Redux Router is an experimental library, it lets you keep entirely the state of your URL inside your redux store.
 * It has the same API with React Router API but has a smaller community support than react-router.
 *
 * React Router Redux creates a binding between your redux app and react-router and it keeps them in sync.
 * Without this binding, you will not be able to rewind the actions with Time Travel.
 */
const checkForRedirect = (Component, urlTranslations) => {
    const { ROOT, SIGNUP, STEP } = urlTranslations;
    const BASE_PATH = `/${ROOT}/${SIGNUP}`;

    class RedirectComponent extends React.Component {
        static contextTypes = {
            router: PropTypes.object.isRequired
        };

        UNSAFE_componentWillReceiveProps(nextProps) {
            const nextStepId = _.get(nextProps, 'ui.activeStep.id');
            const { location } = nextProps;

            const queryParameters = _.get(location, 'search', '');

            const reduxNextStep = _.find(ALL_STEPS, { id: nextStepId });
            const reduxNextStepIndex =
                _.findIndex(STEPS, {
                    id: nextStepId
                }) + 1;

            const locationNextStepPath = _.last(location.pathname.split('/'));
            const locationIsNextStepPathNumeric =
                locationNextStepPath.includes(STEP);
            const locationNextStepIndex = parseInt(
                locationNextStepPath.replace(`${STEP}-`, '')
            );
            const locationNextStep = locationIsNextStepPathNumeric
                ? STEPS[locationNextStepIndex - 1]
                : _.find(ALL_STEPS, { id: locationNextStepPath });

            if (
                typeof reduxNextStepIndex === 'undefined' ||
                _.get(reduxNextStep, 'id') === _.get(locationNextStep, 'id')
            )
                return;

            if (this.isBrowserNavigation) {
                this.props.selectStep(locationNextStep);
                this.isBrowserNavigation = false;
                return;
            }

            const pathname = !reduxNextStep.isConditional
                ? `${BASE_PATH}/${STEP}-${reduxNextStepIndex}`
                : `${BASE_PATH}/${reduxNextStep.id}`;
            IwTracking.setLatestActiveSignupStep(nextStepId);
            GoogleTagManager.viewPage(pathname);
            this.context.router.push(`${pathname}${queryParameters}`);
        }

        componentDidMount() {
            this.isBrowserNavigation = false;

            this.context.router.listenBefore((location) => {
                // Back or forward buttons in the browsers are considered as POP events in browserHistory API
                this.isBrowserNavigation = location.action === 'POP';
            });
        }

        render() {
            return <Component {...this.props} />;
        }
    }

    const mapStateToProps = ({ fetched, ui }) => ({
        application: fetched.application,
        ui
    });

    const mapDispatchToProps = {
        selectStep: (selectedStep) => selectStep(selectedStep)
    };

    return Redux.connect(
        mapStateToProps,
        mapDispatchToProps
    )(RedirectComponent);
};

export function getCurrentRoutingDefinition() {
    const websiteCode = getCurrentWebsiteCode();
    const routesDefinition = buildRoutes(websiteCode);

    return (
        <>
            <Router
                routes={routesDefinition}
                history={browserHistory}
                onUpdate={() => window.scrollTo(0, 0)}
            />
        </>
    );
}

export function getURL(urlName) {
    const websiteCode = getCurrentWebsiteCode();
    const resolvedURL = getURLLabels(websiteCode)[urlName];
    return `/${resolvedURL}/`;
}

function buildRoutes(websiteCode) {
    const urlLabels = getURLLabels(websiteCode);
    const { ROOT, SIGNUP, STEP } = urlLabels;

    return {
        path: '/',
        component: App,
        childRoutes: [
            {
                path: `${ROOT}/`,
                component: checkForRedirect(MyAccountContainer, urlLabels)
            },
            {
                path: `${ROOT}/${SIGNUP}/${STEP}-:stepId`,
                component: checkForRedirect(SignupContainer, urlLabels)
            },
            {
                path: `${ROOT}/${SIGNUP}/*`,
                component: checkForRedirect(SignupContainer, urlLabels)
            }
        ]
    };
}

export const UK_URL_LABELS = {
    ROOT: 'account',
    SIGNUP: 'signup',
    STEP: 'step',
    BROKER_PORTAL: 'broker/account'
};

export const DE_URL_LABELS = {
    ROOT: 'konto',
    SIGNUP: 'anmeldung',
    STEP: 'schritt',
    BROKER_PORTAL: 'partner/account'
};

export function getURLLabels(websiteCode) {
    switch (websiteCode) {
        case 'de':
            return DE_URL_LABELS;
        case 'uk':
        default:
            return UK_URL_LABELS;
    }
}

function getCurrentWebsiteCode() {
    const rootElement = document.getElementById('root');

    if (
        rootElement &&
        rootElement.attributes &&
        rootElement.attributes['website-code']
    ) {
        return rootElement.attributes['website-code'].value;
    }

    return 'uk';
}
